import { VerbRail, VerbRailItem } from '@components/VerbRail/VerbRail';
import type { MediaActionControl } from '@lib/media/mediaActions';
import { MEDIA_VERB_IDS, verbItem } from '@lib/verbs';
import { Media } from '@typings/Media';
import { observer } from 'mobx-react';
import { useMediaMenuActions } from './useMediaMenuActions';

/** Glyphs built once, not per render. Order and set come from `MEDIA_VERB_IDS`. */
const MEDIA_VERBS = MEDIA_VERB_IDS.map((id) => verbItem(id));

interface MediaVerbRailProps {
  media: Media;
  /** Which surface is rendering it, for `Media Menu Action`'s `control` property. */
  control: MediaActionControl;
  /**
   * `inline` (default) for opaque surfaces — a table row, a modal header. `overlay` for glyphs
   * sitting on a thumbnail or video, where each needs its own frosted disc to stay legible
   * against whatever frame is behind it.
   */
  variant?: 'inline' | 'overlay';
  className?: string;
}

/**
 * The media-scoped verbs as a bare icon rail.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS A SEPARATE COMPONENT FOR A LOAD-BEARING REASON, NOT FOR TIDINESS.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `useMediaMenuActions` calls `useLibraryFolder()` and `useMediaDeleteModal()`, and
 * `MediaDeleteModalProvider` is mounted in exactly one place — `LibraryPage`. Hooks cannot be
 * called conditionally, so a caller that renders on more than one route (`MomentModal` serves
 * the library page, `/search` and `/atlas` from one component) CANNOT call the hook itself: it
 * would throw on the routes with no provider and white-screen the page. Rendering this
 * component behind a condition is fine, because the condition guards a *component*, not a
 * hook. `VideoMenuButton` is safe for the same reason.
 *
 * So: mount this only where the library-page providers exist. THAT SPLIT HAS SINCE HAPPENED —
 * `useMediaVerbs` (`@lib/media`) is the context-free half (the three verbs, `copy-link`,
 * `open-original`, `download`) and `useMediaAdminActions` is the provider-bound one (`rename`
 * via `MediaRenameModal`, `retry-processing`, `delete`). `useMediaMenuActions` is now their
 * composition, which is why this component still can't leave the library page: it calls the
 * composed hook.
 *
 * A rail that needs to work anywhere should read `useMediaVerbs` directly. Nothing needs that
 * today — the surface that did (`MomentModal`) replaced its rail with `MediaActionBar`.
 *
 * The desktop kebab no longer lists these verbs, on any surface that shows a rail — so on
 * desktop this IS the entry point, not a shortcut to one. `MobileMediaMenuDrawer` still carries
 * them, because mobile list view has no rail.
 *
 * Every rail that offers these three verbs mounts this — the grid card (`overlay`, with its own
 * positioning class) and the table row (`inline`). The media preview was the third until it
 * moved to `MediaActionBar`. It is deliberately the only place the `actions` → `VerbRailItem`
 * mapping lives: `VideoCard` used to keep a line-for-line copy, and the cost showed up
 * immediately when `disabledReason` had to be added to both in one commit.
 *
 * `observer` because the gating below is derived from `media.status` inside THIS component's
 * render, not the caller's — `useMediaMenuActions` is what reads it. Without this, a media item
 * finishing indexing would leave the rail dimmed until the parent happened to re-render for some
 * other reason.
 */
export const MediaVerbRail = observer(function MediaVerbRail({
  media,
  control,
  variant = 'inline',
  className,
}: MediaVerbRailProps) {
  // `isModifiable: false` — it gates only rename and delete in the hook, neither of which this
  // rail renders, so there is nothing for a caller to supply.
  const { actions, executeAction } = useMediaMenuActions(media, false);

  const items: VerbRailItem[] = MEDIA_VERBS.flatMap((verb): VerbRailItem[] => {
    // Everything about availability — whether the verb is offered, whether it's inert, and why —
    // comes from `actions`, so the rail reads exactly what the menus do and no surface has to
    // re-derive the rule.
    //
    // The only verb that can be missing is `clip-podcast` without `CHAT_URL`, which is a
    // build-time constant: absent for every row or none, so dropping it can't misalign a column.
    const action = actions.find((candidate) => candidate.id === verb.id);
    if (!action) return [];
    return [
      {
        ...verb,
        label: action.label,
        disabled: action.disabled,
        disabledReason: action.disabledReason,
        onClick: () => void executeAction(verb.id, control),
      },
    ];
  });

  return <VerbRail items={items} variant={variant} className={className} />;
});
